import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Loader2,
  Minus,
  Plus,
  RotateCcw,
  Sparkles,
} from "lucide-react";

import {
  fetchAuthenticatedPrivateMediaBlob,
  getLatestVisualProviderOwnerMaterial,
  getVisualProviderOwnerMaterialPreflight,
  resetVisualProviderOwnerMaterialReferenceOverride,
  runVisualProviderOwnerMaterial,
  saveVisualProviderOwnerMaterialReferenceOverride,
} from "../../lib/brandStudioApi";

const ROLE_LABELS = {
  primary_visual_reference: "основной",
  whole_style_reference: "целостный",
  supporting_reference: "поддерживающий",
};

const PACKING_TIER_LABELS = {
  primary: "прямой Material",
  supporting: "поддерживающий Material",
};

const money = (micros) =>
  typeof micros === "number" ? "$" + (micros / 1_000_000).toFixed(4) : "—";

const errorCode = (problem) => {
  const detail = problem?.response?.data?.detail;
  if (typeof detail === "object" && detail?.code) return detail.code;
  if (typeof detail === "string") return detail;
  return "Не удалось выполнить Material QA.";
};

export default function VisualProviderOwnerMaterialExecutionPanelV1() {
  const [preflight, setPreflight] = useState(null);
  const [loadStatus, setLoadStatus] = useState("loading");
  const [loadError, setLoadError] = useState("");

  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [result, setResult] = useState(null);
  const [runStatus, setRunStatus] = useState("idle");
  const [runError, setRunError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draftReferenceIds, setDraftReferenceIds] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState("");

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewStatus, setPreviewStatus] = useState("idle");

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        const [currentPreflight, latest] = await Promise.all([
          getVisualProviderOwnerMaterialPreflight(),
          getLatestVisualProviderOwnerMaterial(),
        ]);
        if (cancelled) return;
        setPreflight(currentPreflight);
        setResult(latest || null);
        setLoadStatus("ready");
      } catch (problem) {
        if (cancelled) return;
        setLoadError(errorCode(problem));
        setLoadStatus("error");
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const refs = [
      ...(preflight?.references || []),
      ...(preflight?.available_references || []),
    ];
    if (!refs.length) {
      setThumbnailUrls({});
      return undefined;
    }

    let cancelled = false;
    const created = [];

    const load = async () => {
      const entries = await Promise.all(
        refs.map(async (reference) => {
          try {
            const blob = await fetchAuthenticatedPrivateMediaBlob(reference.image_url);
            const url = URL.createObjectURL(blob);
            created.push(url);
            return [reference.reference_id, url];
          } catch {
            return [reference.reference_id, ""];
          }
        }),
      );
      if (cancelled) {
        created.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      setThumbnailUrls(Object.fromEntries(entries));
    };

    load();

    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [preflight]);

  useEffect(() => {
    if (!result?.image_url) {
      setPreviewUrl("");
      setPreviewStatus("idle");
      return undefined;
    }

    let cancelled = false;
    let createdObjectUrl = "";

    setPreviewStatus("loading");

    const load = async () => {
      try {
        const blob = await fetchAuthenticatedPrivateMediaBlob(result.image_url);
        if (cancelled) return;
        createdObjectUrl = URL.createObjectURL(blob);
        setPreviewUrl(createdObjectUrl);
        setPreviewStatus("decoding");
      } catch {
        if (cancelled) return;
        setPreviewStatus("error");
      }
    };

    load();

    return () => {
      cancelled = true;
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
  }, [result?.image_url]);

  const packetShort = useMemo(
    () => (preflight?.packet_fingerprint || "").slice(0, 12),
    [preflight?.packet_fingerprint],
  );

  const allReferenceOptions = useMemo(
    () => [
      ...(preflight?.references || []),
      ...(preflight?.available_references || []),
    ],
    [preflight],
  );

  const beginEditing = () => {
    setDraftReferenceIds(
      (preflight?.references || []).map((reference) => reference.reference_id),
    );
    setSaveError("");
    setSaveStatus("idle");
    setEditing(true);
  };

  const acceptUpdatedPreflight = (value) => {
    setPreflight(value);
    setResult((current) => (
      current
        ? {
            ...current,
            current_packet_match: current.packet_fingerprint === value.packet_fingerprint,
          }
        : current
    ));
    setEditing(false);
    setSaveStatus("success");
  };

  const saveSelection = async () => {
    if (!draftReferenceIds.length || draftReferenceIds.length > 8) return;
    setSaveStatus("saving");
    setSaveError("");
    try {
      acceptUpdatedPreflight(
        await saveVisualProviderOwnerMaterialReferenceOverride(draftReferenceIds),
      );
    } catch (problem) {
      setSaveError(errorCode(problem));
      setSaveStatus("error");
    }
  };

  const resetSelection = async () => {
    setSaveStatus("saving");
    setSaveError("");
    try {
      acceptUpdatedPreflight(
        await resetVisualProviderOwnerMaterialReferenceOverride(),
      );
    } catch (problem) {
      setSaveError(errorCode(problem));
      setSaveStatus("error");
    }
  };

  const runOne = async () => {
    if (!preflight?.ready || runStatus === "running") return;

    const requestId =
      globalThis.crypto?.randomUUID?.() ||
      "00000000-0000-4000-8000-" + String(Date.now()).slice(-12).padStart(12, "0");

    setRunStatus("running");
    setRunError("");

    try {
      const value = await runVisualProviderOwnerMaterial({
        requestId,
        expectedPacketFingerprint: preflight.packet_fingerprint,
      });
      setResult(value);
      setRunStatus("success");
    } catch (problem) {
      setRunError(errorCode(problem));
      setRunStatus("error");

      if (problem?.response?.status === 409) {
        try {
          const refreshed = await getVisualProviderOwnerMaterialPreflight();
          setPreflight(refreshed);
        } catch {
          // Keep the original server error visible.
        }
      }
    }
  };

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="md1-owner-material-panel"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        ПРОВЕРКА ВЛАДЕЛЬЦА · MD1
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Материалы — живой тест владельца
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Это платный Owner QA тест Material Direction. Экран сам ничего не генерирует:
        Prodia вызывается только после Вашего явного нажатия на платную кнопку.
      </p>

      {loadStatus === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Проверяю текущий пакет материалов…
        </div>
      )}

      {loadStatus === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {loadError}
        </div>
      )}

      {preflight && (
        <div className="mt-6 space-y-4 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} />
              {preflight.ready ? "пакет материалов готов" : "пакет материалов заблокирован"}
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              Prodia · FLUX.2 Klein 4B · пакет {packetShort || "—"}
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Технический запуск
              </div>
              <div className="mt-2 text-[11px] leading-5 text-[#655141]">
                {preflight.reference_count} референсов для провайдера
                <br />
                {preflight.width}×{preflight.height} · {preflight.steps} шага
                <br />
                Стилевой пресет: не передаётся
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Стоимость
              </div>
              <div className="mt-2 text-[11px] leading-5 text-[#655141]">
                Опубликованная оценка: {money(preflight.published_estimate_micros)}
                <br />
                Аварийный лимит: {money(preflight.hard_cap_micros)}
                <br />
                Фактическая цена — по ответу Prodia
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                Приватность
              </div>
              <div className="mt-2 text-[11px] leading-5 text-[#655141]">
                Риск Owner QA: {preflight.owner_qa_risk_accepted ? "принят" : "не принят"}
                <br />
                Использование для клиентов: не разрешено
                <br />
                Автозапуск: отсутствует
              </div>
            </article>
          </div>

          {!!preflight.blocking_reasons?.length && (
            <div className="rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[11px] leading-5 text-[#6B443A]">
              Блокирует: {preflight.blocking_reasons.join(", ")}
            </div>
          )}

          <article className="rounded-[16px] border border-[#DED0BC] bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                  Именно эти {preflight.reference_count} референсов будут отправлены
                </div>
                <div className="mt-1 text-[10px] text-[#8A745E]">
                  {preflight.owner_override_active
                    ? "Сохранён выбор владельца"
                    : "Автоматический выбор системы"}
                </div>
              </div>
              <button
                type="button"
                onClick={beginEditing}
                className="inline-flex min-h-10 items-center rounded-full border border-[#BCA78C] px-4 text-[10.5px] text-[#5A4432]"
                data-testid="md12-edit-selection"
              >
                Изменить выбор
              </button>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(preflight.references || []).map((reference) => (
                <div
                  key={reference.reference_id}
                  className="overflow-hidden rounded-[12px] border border-[#E4D8C8] bg-[#FFFDF9]"
                >
                  <div className="aspect-[4/3] bg-[#F3EDE4]">
                    {thumbnailUrls[reference.reference_id] ? (
                      <img
                        src={thumbnailUrls[reference.reference_id]}
                        alt={"Референс №" + reference.user_reference_number}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-[9.5px] text-[#927A5B]">
                        Загружаю…
                      </div>
                    )}
                  </div>
                  <div className="p-2.5 text-[9.5px] leading-4 text-[#6C5745]">
                    Референс №{reference.user_reference_number}
                    {" · "}порядок отправки {reference.provider_order}
                    {" · "}ранг workflow {reference.workflow_rank}
                    <br />
                    {PACKING_TIER_LABELS[reference.packing_tier] || reference.packing_tier}
                    {" · "}
                    {ROLE_LABELS[reference.role_in_execution] || reference.role_in_execution}
                    {reference.identity_content_present ? " · айдентика только как контекст" : ""}
                    <br />
                    <span className="text-[#8A6D53]">
                      {reference.provenance === "owner_added"
                        ? "добавлен владельцем"
                        : "автоматический выбор"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {editing && (
              <div className="mt-5 rounded-[14px] border border-[#D8C7B2] bg-[#FFF9F1] p-4" data-testid="md12-reference-editor">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-[11px] font-medium text-[#4C392B]">
                    Текущий набор · {draftReferenceIds.length}/8
                  </div>
                  <button
                    type="button"
                    onClick={resetSelection}
                    disabled={saveStatus === "saving"}
                    className="inline-flex items-center gap-1.5 text-[10.5px] text-[#755C47] disabled:opacity-40"
                    data-testid="md12-reset-selection"
                  >
                    <RotateCcw size={12} />
                    Вернуть автоматический выбор
                  </button>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {draftReferenceIds.map((referenceId) => {
                    const reference = allReferenceOptions.find(
                      (item) => item.reference_id === referenceId,
                    );
                    if (!reference) return null;
                    return (
                      <div key={referenceId} className="overflow-hidden rounded-[12px] border border-[#D8C7B2] bg-white">
                        <div className="aspect-[4/3] bg-[#F3EDE4]">
                          {thumbnailUrls[referenceId] && (
                            <img src={thumbnailUrls[referenceId]} alt={"Референс №" + reference.user_reference_number} className="h-full w-full object-cover" />
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 p-2.5 text-[9.5px] text-[#6C5745]">
                          <span>Референс №{reference.user_reference_number}</span>
                          <button
                            type="button"
                            onClick={() => setDraftReferenceIds((values) => values.filter((value) => value !== referenceId))}
                            className="inline-flex items-center gap-1 text-[#874F45]"
                          >
                            <Minus size={11} /> Убрать
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5 text-[11px] font-medium text-[#4C392B]">
                  Остальные референсы проекта
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {allReferenceOptions
                    .filter((reference) => !draftReferenceIds.includes(reference.reference_id))
                    .map((reference) => (
                      <div key={reference.reference_id} className="overflow-hidden rounded-[12px] border border-[#E4D8C8] bg-white">
                        <div className="aspect-[4/3] bg-[#F3EDE4]">
                          {thumbnailUrls[reference.reference_id] && (
                            <img src={thumbnailUrls[reference.reference_id]} alt={"Референс №" + reference.user_reference_number} className="h-full w-full object-cover" />
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 p-2.5 text-[9.5px] text-[#6C5745]">
                          <span>
                            Референс №{reference.user_reference_number}
                            {reference.provenance === "owner_excluded" ? " · исключён владельцем" : ""}
                          </span>
                          <button
                            type="button"
                            disabled={draftReferenceIds.length >= 8}
                            onClick={() => setDraftReferenceIds((values) => [...values, reference.reference_id])}
                            className="inline-flex items-center gap-1 text-[#526044] disabled:opacity-35"
                          >
                            <Plus size={11} /> Добавить
                          </button>
                        </div>
                      </div>
                    ))}
                </div>

                {!draftReferenceIds.length && (
                  <div className="mt-3 text-[10.5px] text-[#874F45]">
                    Для live execution нужен минимум один референс.
                  </div>
                )}
                {saveError && <div className="mt-3 text-[10.5px] text-[#874F45]">{saveError}</div>}
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={saveSelection}
                    disabled={!draftReferenceIds.length || draftReferenceIds.length > 8 || saveStatus === "saving"}
                    className="inline-flex min-h-10 items-center rounded-full bg-[#5B4635] px-4 text-[10.5px] text-white disabled:opacity-40"
                    data-testid="md12-save-selection"
                  >
                    {saveStatus === "saving" ? "Сохраняю…" : "Сохранить выбор"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    disabled={saveStatus === "saving"}
                    className="inline-flex min-h-10 items-center rounded-full border border-[#BCA78C] px-4 text-[10.5px] text-[#5A4432] disabled:opacity-40"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            )}
          </article>

          <div className="rounded-[14px] border border-[#E0C4A6] bg-[#FFF4E8] p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#8A6D53]">
              <AlertTriangle size={13} />
              Перед платным вызовом
            </div>
            <p className="mt-2 text-[11.5px] leading-5 text-[#655141]">
              При запуске эти {preflight.reference_count} изображений будут отправлены в Prodia. Это платный
              Owner QA вызов. Публично подтверждённых условий retention, training
              exclusion и deletion window для reference inputs у нас нет; ранее
              раскрытый риск принят только для Owner QA.
            </p>

            <button
              type="button"
              onClick={runOne}
              disabled={!preflight.ready || runStatus === "running"}
              className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full border border-[#8A6D53] bg-white px-4 text-[11px] text-[#4B3829] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="md1-owner-run-material"
            >
              {runStatus === "running" ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              {runStatus === "running"
                ? "Отправляю " + preflight.reference_count + " референсов в Prodia…"
                : "Запустить платный Material QA · " +
                  preflight.reference_count +
                  " референсов · ≈ " +
                  money(preflight.published_estimate_micros)}
            </button>

            {runStatus === "error" && (
              <div className="mt-3 text-[11px] leading-5 text-[#874F45]">
                {runError}
              </div>
            )}
          </div>

          {result && (
            <article className="rounded-[16px] border border-[#DED0BC] bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                  Последний результат Material Direction
                </div>
                <div className="text-[9.5px] text-[#8A745E]">
                  {result.current_packet_match
                    ? "соответствует текущему пакету"
                    : "относится к предыдущему пакету"}
                </div>
              </div>

              {(previewStatus === "loading" || previewStatus === "decoding") && (
                <div className="relative mt-3 flex min-h-[240px] items-center justify-center overflow-hidden rounded-[14px] border border-[#DED0BC] bg-[#F5F0E8] text-[11px] text-[#796652]">
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Загружаю закрытое изображение…
                  {previewStatus === "decoding" && previewUrl && (
                    <img
                      src={previewUrl}
                      alt=""
                      aria-hidden="true"
                      className="pointer-events-none absolute h-px w-px opacity-0"
                      onLoad={() => setPreviewStatus("ready")}
                      onError={() => setPreviewStatus("error")}
                      data-testid="md1-private-preview-probe"
                    />
                  )}
                </div>
              )}

              {previewStatus === "ready" && previewUrl && (
                <img
                  src={previewUrl}
                  alt="Material Direction Owner QA"
                  className="mt-3 max-h-[720px] w-full rounded-[14px] border border-[#DED0BC] bg-[#F5F0E8] object-contain"
                  onError={() => setPreviewStatus("error")}
                  data-testid="md1-private-preview"
                />
              )}

              {previewStatus === "error" && (
                <div className="mt-3 rounded-[14px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[11px] text-[#874F45]">
                  Закрытое изображение не загрузился. Сохранённый результат не нужно генерировать повторно.
                </div>
              )}

              <div className="mt-3 text-[10.5px] leading-5 text-[#655141]">
                Фактическая цена: {money(result.actual_cost_micros)}
                {" · "}референсов: {result.reference_ids?.length || 0}
                {" · "}повторно использован: {result.reused ? "да" : "нет"}
                <br />
                Технический статус: успешно
                {" · "}Статус качества: ждёт оценки владельца
              </div>
            </article>
          )}

          <details className="rounded-[14px] border border-[#DED0BC] bg-white">
            <summary className="cursor-pointer px-4 py-3 text-[11.5px] font-medium text-[#4C392B]">
              Показать текущую инструкцию для провайдера
            </summary>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap border-t border-[#EEE4D6] p-4 text-[9px] leading-4 text-[#5F4A39]">
              {preflight.instruction_text}
            </pre>
          </details>
        </div>
      )}
    </section>
  );
}
